import { ConfirmDestructiveDialog } from '@/shared/components/ConfirmDestructiveDialog';

/**
 * Confirms deleting a folder — which is not one delete but as many as the
 * folder holds, at every depth.
 *
 * The count is read before the question is asked, and the confirmation stays
 * unavailable until it is known: "delete this folder" means something very
 * different for a folder of one file and a folder of four hundred, and the
 * reader is the one who has to decide which they are looking at.
 */
export function DeleteFolderDialog({
  folderName,
  fileCount,
  isCounting,
  countFailed,
  isDeleting,
  error,
  open,
  onConfirm,
  onCancel,
}: {
  folderName: string;
  fileCount: number | undefined;
  isCounting: boolean;
  countFailed: boolean;
  isDeleting: boolean;
  error?: string | null;
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <ConfirmDestructiveDialog
      open={open}
      title={<>Delete &ldquo;{folderName}&rdquo;?</>}
      confirmLabel="Delete folder"
      isPending={isDeleting}
      error={error}
      confirmDisabled={isCounting || countFailed}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      {isCounting
        ? `Reading what is inside “${folderName}”…`
        : countFailed
          ? `What is inside “${folderName}” could not be read, so it cannot be deleted safely. Try again in a moment.`
          : fileCount === 0
            ? // True of a folder nothing was ever put in, and of one
              // holding only empty folders — both hold no files, and
              // both lose whatever structure they have.
              `“${folderName}” holds no files. Deleting it removes the folder and anything inside it.`
            : `This will permanently delete “${folderName}” and the ${String(fileCount)} ${fileCount === 1 ? 'file' : 'files'} inside it, at every depth. This cannot be undone.`}
    </ConfirmDestructiveDialog>
  );
}
